import type { BadgeVariant } from '../../components/ui/Badge'
import { formatMoney } from '../layout-generator/status'
import type {
  RunDetail,
  RunScope,
  RunState,
  RunSummary,
  SheetStatus,
  SlideCheck,
  SlideReview,
  SlideStatus,
  StageSheet,
  Stage,
  StageSlide,
  ThemeIntensity,
} from './types'

/** Вкладки запусков на странице «Контент-воронки»: отдельной страницы списка больше нет. */
export type SeriesTabKey = 'series-theme' | 'series-sku'
export const seriesTabOf = (scope: RunScope | undefined): SeriesTabKey => (scope === 'sku' ? 'series-sku' : 'series-theme')
export const seriesListPath = (scope?: RunScope) => `/content-funnels?tab=${seriesTabOf(scope)}`
export const SERIES_LIST_PATH = seriesListPath('theme')
export const SERIES_NEW_PATH = '/content-funnel-series/new'
export const seriesRunPath = (runId: string) => `/content-funnel-series/${encodeURIComponent(runId)}`

/** Группы состояний для сводки над таблицей: что ждёт человека, что идёт, что готово, что сломалось. */
export type RunStateGroup = 'attention' | 'working' | 'done' | 'failed' | 'cancelled'

export function runStateGroup(state: RunState): RunStateGroup {
  if (state === 'draft_ready' || state === 'final_ready') return 'attention'
  if (state === 'registered' || state === 'published') return 'done'
  if (state === 'failed') return 'failed'
  if (state === 'cancelled') return 'cancelled'
  return 'working'
}

/** У готового этапа запуска есть бракованные слайды: запуск не упал, но чистовик с браком не утвердить. */
export function brokenSlideCount(run: Pick<RunSummary, 'state' | 'failedSlides'>): number {
  if (run.state !== 'draft_ready' && run.state !== 'final_ready') return 0
  return run.failedSlides?.length ?? 0
}

/** Подсказка «что дальше» под состоянием в таблице; null — бейджа достаточно. */
export function runNextStep(run: RunSummary): string | null {
  const sku = run.scope === 'sku'
  const broken = brokenSlideCount(run)
  if (broken > 0) return `Есть бракованные слайды: ${broken}`
  switch (run.state) {
    case 'draft_ready':
      return 'Проверьте черновик и запустите чистовик'
    case 'final_ready':
      return sku ? 'Проверьте слайды и утвердите публикацию' : 'Проверьте чистовик и создайте воронку'
    case 'approved':
      return 'Слайды уходят в кабинеты'
    case 'published': {
      const summary = run.publicationSummary
      return summary && summary.total > 0 ? `В кабинетах: ${summary.ok} из ${summary.total}` : null
    }
    case 'failed':
      return run.error?.message || 'Откройте запуск, чтобы увидеть причину'
    default:
      return null
  }
}

/** Уровни насыщенности темой (CONTRACT §10). */
export const INTENSITY_LEVELS: { value: ThemeIntensity; label: string; description: string }[] = [
  {
    value: 'minimal',
    label: 'Минимально',
    description: 'Спокойный градиентный фон без мотивов и героев; не больше одного мелкого акцента у края.',
  },
  {
    value: 'light',
    label: 'Легко',
    description: 'Градиент и редкие бледные мотивы; один маленький герой сбоку на части слайдов, 2–3 акцента у нижнего края.',
  },
  {
    value: 'balanced',
    label: 'Умеренно',
    description: 'Мягкий тематический орнамент по краям; один герой не выше четверти слайда, до 5 мелких акцентов.',
  },
  {
    value: 'rich',
    label: 'Насыщенно',
    description: 'Лёгкая тематическая сцена и до двух героев; товар всё равно остаётся главным на слайде.',
  },
]

export const DEFAULT_INTENSITY: ThemeIntensity = 'light'

export const RUN_STATE_META: Record<RunState, { label: string; variant: BadgeVariant }> = {
  draft_pending: { label: 'Рисуется черновик', variant: 'yellow' },
  draft_ready: { label: 'Черновик готов', variant: 'blue' },
  final_pending: { label: 'Рисуется чистовик', variant: 'yellow' },
  final_ready: { label: 'Чистовик готов', variant: 'indigo' },
  registering: { label: 'Создаётся воронка', variant: 'yellow' },
  registered: { label: 'Воронка создана', variant: 'green' },
  approved: { label: 'Утверждено', variant: 'yellow' },
  published: { label: 'Опубликовано', variant: 'green' },
  failed: { label: 'Сбой', variant: 'red' },
  cancelled: { label: 'Отменён', variant: 'gray' },
}

export function runStateMeta(state: string): { label: string; variant: BadgeVariant } {
  return RUN_STATE_META[state as RunState] ?? { label: state, variant: 'gray' }
}

/** Запуск ещё работает: идёт лист, нарезка, регистрация воронки или публикация SKU в кабинеты. */
export function isRunPending(state: string): boolean {
  return state === 'draft_pending' || state === 'final_pending' || state === 'registering' || state === 'approved'
}

export const SHEET_STATUS_LABEL: Record<SheetStatus, string> = {
  waiting: 'Ждёт своей очереди',
  queued: 'В очереди',
  generating: 'Рисуется',
  cutting: 'Нарезается на слайды',
  ready: 'Готов',
  failed: 'Сбой',
  cancelled: 'Отменён',
}

export function sheetStatusVariant(status: SheetStatus): BadgeVariant {
  if (status === 'ready') return 'green'
  if (status === 'failed') return 'red'
  if (status === 'cancelled') return 'gray'
  if (status === 'waiting') return 'gray'
  return 'yellow'
}

export function sheetHasProduct(sheet: StageSheet): boolean {
  return sheet.slides.some((slide) => slide.kind === 'template')
}

/** «Лист 1 — с товаром», «Лист 2 — общие». */
export function sheetTitleOf(index: number, hasProduct: boolean): string {
  return `Лист ${index + 1} — ${hasProduct ? 'с товаром' : 'общие'}`
}

export function sheetTitle(sheet: StageSheet): string {
  // Готовые картинки (SKU): деления «с товаром / общие» нет, каждый слайд уже собран под товар.
  if (sheet.slides.some((slide) => slide.kind === 'image')) return `Лист ${sheet.index + 1}`
  return sheetTitleOf(sheet.index, sheetHasProduct(sheet))
}

export const SLIDE_STATUS_LABEL: Record<SlideStatus, string> = {
  pending: 'Ещё не готов',
  ok: 'Нарезка в порядке',
  warning: 'Предупреждение',
  failed: 'Ошибка',
}

export function slideStatusVariant(status: SlideStatus): BadgeVariant {
  if (status === 'ok') return 'green'
  if (status === 'warning') return 'yellow'
  if (status === 'failed') return 'red'
  return 'gray'
}

/** Коды проверок нарезки (воркер `sheet.py`, проверка надписей `review.py`) — понятными названиями. */
export const CHECK_LABELS: Record<string, string> = {
  SLOT_FOUND: 'Зона печати найдена',
  SLOT_AREA: 'Размер зоны печати',
  SLOT_CONVEX: 'Форма зоны печати',
  CHROMA_OUTSIDE_SLOT: 'Нет лишних пятен цвета зоны',
  CELL_BOUNDS: 'Границы слайда на листе',
  LOGO_PLACEMENT: 'Место логотипа',
  LOGO_OVERLAPS_SLOT: 'Логотип не закрывает зону печати',
  GRID_UNSUPPORTED_SLOT: 'Сетка 1 см для этой зоны',
  TEXT_OK: 'Надписи совпали с набором',
  TEXT_MISMATCH: 'Надписи расходятся с набором',
}

export function checkLabel(check: SlideCheck): string {
  return CHECK_LABELS[check.code] ?? check.code
}

/** Число расхождений надписей: неверные тексты плюс лишние надписи (не меньше 1 при статусе mismatch). */
export function mismatchCount(review: SlideReview): number {
  const wrong = (review.texts ?? []).filter((item) => !item.ok).length
  const extra = (review.extraText ?? []).length
  return Math.max(1, wrong + extra)
}

export function reviewBadge(review: SlideReview | null): { label: string; variant: BadgeVariant } | null {
  if (!review) return null
  if (review.status === 'ok') return { label: 'Надписи верны', variant: 'green' }
  if (review.status === 'mismatch') return { label: `Надписи: ${mismatchCount(review)} ${plural(mismatchCount(review), 'расхождение', 'расхождения', 'расхождений')}`, variant: 'yellow' }
  if (review.status === 'pending') return { label: 'Надписи проверяются', variant: 'gray' }
  return { label: 'Надписи не проверены', variant: 'gray' }
}

export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return many
  if (last === 1) return one
  if (last >= 2 && last <= 4) return few
  return many
}

export const REVISION_STATUS_LABEL: Record<string, string> = {
  queued: 'В очереди',
  leased: 'Выполняется',
  running: 'Выполняется',
  succeeded: 'Принята',
  failed: 'Не удалась',
  uncertain: 'Исход неизвестен',
  cancelled: 'Отменена',
}

export function revisionStatusVariant(status: string): BadgeVariant {
  if (status === 'succeeded') return 'green'
  if (status === 'failed' || status === 'uncertain') return 'red'
  if (status === 'cancelled') return 'gray'
  return 'yellow'
}

/** Код сбоя запуска словами; неизвестный код — сообщение сервера. */
export const RUN_ERROR_LABELS: Record<string, string> = {
  SERIES_SLIDES_FAILED: 'Нарезка не нашла зону печати на одном или нескольких слайдах',
  DISPATCH_OUTCOME_UNKNOWN: 'Неизвестно, принял ли запрос провайдер: повторять автоматически нельзя',
  PROVIDER_REJECTED: 'Провайдер отклонил запрос',
  STALE_RUN_REVISION: 'Запуск перешёл на новую ревизию',
}

// --- деньги и даты -----------------------------------------------------------

/** Деньги в валюте оценки; неизвестная цена — «стоимость уточняется», а не «0». */
export function usd(value: number | null | undefined, currency: string | null | undefined = 'USD'): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return formatMoney(value, currency || 'USD')
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date)
}

// --- главное действие запуска -------------------------------------------------

export type MainActionKind = 'final' | 'register' | 'approve' | 'publishing' | 'published' | 'open' | 'retry-draft' | 'retry-final' | 'retry-register' | 'working' | 'none'

function allSheetsReady(sheets: StageSheet[]): boolean {
  return sheets.length > 0 && sheets.every((sheet) => sheet.status === 'ready' && sheet.slides.every((slide) => slide.status !== 'failed'))
}

/**
 * Что делать после сбоя (CONTRACT §4): сбой регистрации повторяется регистрацией в той же ревизии;
 * сбой чистовика при готовом черновике — чистовиком; иначе — новым черновиком.
 */
export function retryKind(detail: RunDetail): 'retry-draft' | 'retry-final' | 'retry-register' {
  const operationId = detail.run.error?.operationId
  const operation = operationId ? detail.operations.find((item) => item.id === operationId) : undefined
  if (operation?.type === 'register_funnel') return 'retry-register'
  // У запуска без черновика (`stages: ['final']`) повторять нечего, кроме чистовика.
  if (isFinalOnlyRun(detail)) return 'retry-final'
  if (operation?.stage === 'final' && allSheetsReady(detail.stages.draft.sheets)) return 'retry-final'
  return 'retry-draft'
}

/** Запуск на один артикул (CONTRACT §21): готовые картинки утверждаются и уходят в визуалы товара. */
export function isSkuRun(detail: RunDetail): boolean {
  return detail.run.scope === 'sku'
}

/** Результат запуска — готовые картинки (`output: images`): превью «на макете» не строятся. */
export function isImagesRun(detail: RunDetail): boolean {
  return detail.run.output === 'images'
}

/** У запуска нет черновика (`stages: ['final']`, CONTRACT §20.5): сразу чистовик. */
export function isFinalOnlyRun(detail: RunDetail): boolean {
  const stages = detail.run.slideSet?.settings?.stages
  return Array.isArray(stages) && stages.length === 1 && stages[0] === 'final'
}

/**
 * Бракованные слайды (статус `failed`) того этапа, на котором запуск стоит «готов»: draft_ready — черновик,
 * final_ready — чистовик. `null` — брака нет. Слайд после удачной правки браком не считается (сервер отдаёт текущую версию).
 */
export function brokenSlides(detail: RunDetail): { stage: Stage; slides: StageSlide[] } | null {
  const { state } = detail.run
  const stage: Stage | null = state === 'draft_ready' ? 'draft' : state === 'final_ready' ? 'final' : null
  if (!stage) return null
  const view = detail.stages[stage]
  const all = view.slides ?? view.sheets.flatMap((sheet) => sheet.slides)
  const slides = all.filter((slide) => slide.status === 'failed')
  return slides.length > 0 ? { stage, slides } : null
}

export function mainActionKind(detail: RunDetail): MainActionKind {
  const { state } = detail.run
  if (state === 'approved') return 'publishing'
  if (state === 'published') return 'published'
  if (isRunPending(state)) return 'working'
  if (state === 'draft_ready') return 'final'
  if (state === 'final_ready') return isSkuRun(detail) ? 'approve' : 'register'
  if (state === 'registered') return 'open'
  if (state === 'failed') return retryKind(detail)
  return 'none'
}

export const REDRAFT_STATES: RunState[] = ['draft_ready', 'final_ready', 'failed', 'cancelled']
export const CANCEL_STATES: RunState[] = ['draft_pending', 'draft_ready', 'final_pending', 'final_ready', 'failed']
export const REVISE_STATES: RunState[] = ['draft_ready', 'final_ready']

/**
 * Править слайд можно в «черновик готов» / «чистовик готов»; после утверждения SKU — только чистовик
 * (правка вернёт запуск на утверждение).
 */
export function canReviseSlide(state: RunState, stage: Stage): boolean {
  if (REVISE_STATES.includes(state)) return true
  return stage === 'final' && (state === 'approved' || state === 'published')
}

export function anySlideBusy(detail: RunDetail): boolean {
  return (['draft', 'final'] as const).some((stage) =>
    detail.stages[stage].sheets.some((sheet) => sheet.slides.some((slide) => slide.busy)),
  )
}

/** Опрашиваем, пока запуск в работе или у слайда идёт правка/проверка. */
export function runNeedsPolling(detail: RunDetail | null): boolean {
  if (!detail) return false
  return isRunPending(detail.run.state) || anySlideBusy(detail)
}

/** Коды макетов для переключателя превью: из запуска, иначе из файлов нарезки. */
export function previewCodes(detail: RunDetail, stage: 'draft' | 'final'): string[] {
  const available = new Set<string>()
  for (const sheet of detail.stages[stage].sheets) {
    for (const slide of sheet.slides) {
      for (const code of Object.keys(slide.files?.previews ?? {})) available.add(code)
    }
  }
  const ordered = detail.run.previewDesignCodes.filter((code) => available.has(code))
  for (const code of [...available].sort()) if (!ordered.includes(code)) ordered.push(code)
  return ordered
}

/** Адрес картинки плитки слайда: готовая картинка SKU (`final`), превью шаблона на выбранном макете, статичный `static.png`, иначе кадр листа. */
export function slideImageUrl(slide: StageSlide, previewCode: string | null): string | null {
  const files = slide.files
  if (!files) return null
  if (files.final) return files.final
  if (slide.kind === 'static') return files.static || files.cell
  if (previewCode && files.previews[previewCode]) return files.previews[previewCode]
  return files.cell
}
